import { useState } from 'react';

import {
  Check,
  ChevronsUpDown,
  Loader2,
  Package,
  Send,
  Upload,
} from 'lucide-react';

import { useGetSandbox } from '@/api/getSandbox';
import { useGetSandboxes } from '@/api/getSandboxes';
import { AppTooltip } from '@/components/appTooltip';
import { usePopup } from '@/components/confirmation';
import { PermissionCheck } from '@/components/permissionCheck';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { StateBadge } from '@/components/ui/stateBadge';
import { useCanEnterSandbox } from '@/hooks/useCanEnterSandbox';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useGame } from '@/hooks/useGame';
import { useSandboxSwitch } from '@/hooks/useGameSandboxSwitch';
import { usePermissionCheck } from '@/hooks/usePermissionCheck';
import { toaster } from '@/lib/toaster';
import { Permissions } from '@/models/permission';
import { type SandboxPublishStatus } from '@/models/sandbox';
import { useSandboxContext } from '@/providers';

import { SandboxPublishSummaryDialog } from './sandboxPublishSummaryDialog';
import { useExportSandbox } from '../api/exportSandbox';
import {
  type PublishSandboxResponse,
  usePublishSandbox,
} from '../api/publishSandbox';

/**
 * Only includes counts that are greater than 0.
 */
const buildPendingCountsTooltip = (status: SandboxPublishStatus): string => {
  const parts: string[] = [];
  if (status.ready_items_count - status.ready_season_passes_count > 0) {
    parts.push(
      `${status.ready_items_count - status.ready_season_passes_count} ready catalog item(s)`,
    );
  }
  if (status.ready_snapshots_count > 0) {
    parts.push(`${status.ready_snapshots_count} ready snapshot(s)`);
  }
  if (status.ready_gifts_count > 0) {
    parts.push(`${status.ready_gifts_count} ready gift(s)`);
  }
  if (status.ready_season_passes_count > 0) {
    parts.push(`${status.ready_season_passes_count} ready season pass(es)`);
  }
  return parts.join(', ');
};

/**
 * Only includes counts that are greater than 0.
 */
const buildPublishConfirmationText = (
  status: SandboxPublishStatus,
): string[] => {
  const parts: string[] = [];
  if (status.ready_items_count - status.ready_season_passes_count > 0) {
    parts.push(
      `${status.ready_items_count - status.ready_season_passes_count} catalog item(s)`,
    );
  }
  if (status.ready_snapshots_count > 0) {
    parts.push(`${status.ready_snapshots_count} snapshot(s)`);
  }
  if (status.ready_gifts_count > 0) {
    parts.push(`${status.ready_gifts_count} gift(s)`);
  }
  if (status.ready_season_passes_count > 0) {
    parts.push(`${status.ready_season_passes_count} season pass(es)`);
  }
  return parts;
};

interface SandboxBreadcrumbDropdownProps {
  sandboxCode: string;
}

export const SandboxBreadcrumbDropdown = ({
  sandboxCode,
}: SandboxBreadcrumbDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [publishResult, setPublishResult] =
    useState<PublishSandboxResponse | null>(null);

  const { selectedSandbox, gameCode } = useSandboxContext();
  const game = useGame();
  const { handleInfo } = useErrorHandler();
  const { showPopup } = usePopup();

  const { switchSandbox } = useSandboxSwitch({
    onBeforeSwitch: () => {
      setIsOpen(false);
    },
  });

  const { data: sandboxes } = useGetSandboxes({ game_code: game.game_code });

  const currentSandbox = sandboxes?.sandboxes.find(
    (sandbox) => sandbox.code === selectedSandbox,
  );

  // GET /v1/sandbox/{id} is gated on sandboxes:read (admin-api router.go), and this
  // breadcrumb renders on every Sandbox-scoped page — so without the code the query
  // would 403 on every navigation. Its only consumer is the publish status below.
  //
  // Known limitation: a Grant carrying sandboxes:publish without sandboxes:read
  // can't reach publish_status at all (the endpoint demands read), so its Publish
  // entry stays disabled on "No pending changes" even when there are some. Fixing
  // it needs publish_status on the auth-only sandbox list, not a wider gate here.
  const canReadSandbox = usePermissionCheck({
    oneOf: [Permissions.sandboxes.read],
  });

  // Fetch single sandbox for publish_status (enables query invalidation to refresh breadcrumb)
  const { data: sandboxDetail } = useGetSandbox(
    { sandboxId: currentSandbox?.id ?? '' },
    { enabled: !!currentSandbox?.id && canReadSandbox },
  );

  const canEnterSandbox = useCanEnterSandbox();

  const publishStatus = sandboxDetail?.publish_status ?? null;
  const canPublish = publishStatus?.has_pending === true;

  const exportMutation = useExportSandbox({
    mutationConfig: {
      onSuccess: () => {
        setIsOpen(false);
      },
    },
  });

  const publishMutation = usePublishSandbox();

  const isLoading = exportMutation.isPending || publishMutation.isPending;

  const handleSandboxChange = (newSandbox: string) => {
    switchSandbox(gameCode, selectedSandbox, newSandbox);
  };

  const handleExport = () => {
    if (!currentSandbox) {
      handleInfo('No sandbox selected');
      return;
    }

    exportMutation.mutate({ sandboxId: currentSandbox.id });
  };

  const handlePublish = async () => {
    if (!currentSandbox) {
      handleInfo('No sandbox selected');
      return;
    }

    setIsOpen(false);

    const confirmed = await showPopup({
      title: 'Confirm Publish',
      content: (
        <div className="space-y-2">
          <p>
            Are you sure you want to publish sandbox{' '}
            <strong>{currentSandbox.name}</strong>?
          </p>
          {publishStatus && publishStatus.has_pending && (
            <ul className="text-sm text-muted-foreground">
              This will publish :
              {buildPublishConfirmationText(publishStatus).map((part) => (
                <li key={part} className="list-disc list-inside font-bold">
                  {part}
                </li>
              ))}
            </ul>
          )}
        </div>
      ),
      confirmText: 'Publish',
      cancelText: 'Cancel',
      onConfirm: async () => {
        const result = await publishMutation.mutateAsync({
          sandboxId: currentSandbox.id,
          gameCode: game.game_code,
        });
        toaster.success('Sandbox published successfully');
        setPublishResult(result);
      },
    });

    if (!confirmed) {
      return;
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Package size={16} />
      {publishStatus?.has_pending && (
        <AppTooltip title={buildPendingCountsTooltip(publishStatus)}>
          <div>
            <StateBadge state="dirty" />
          </div>
        </AppTooltip>
      )}

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" disabled={isLoading}>
            <span className="truncate max-w-48">
              {currentSandbox?.name ?? sandboxCode}
            </span>
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ChevronsUpDown />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-xs p-0">
          <Command>
            <CommandInput placeholder="Search sandbox..." />
            <CommandList className="max-h-48 overflow-y-auto">
              <CommandEmpty>No sandbox found.</CommandEmpty>
              {currentSandbox && (
                <>
                  <CommandGroup heading="Current">
                    <CommandItem
                      value={currentSandbox.name}
                      onSelect={() => {
                        handleSandboxChange(currentSandbox.code);
                      }}
                    >
                      <Check className="mr-2 h-4 w-4 opacity-100" />
                      {currentSandbox.name}
                    </CommandItem>
                  </CommandGroup>
                  <CommandSeparator />
                </>
              )}
              <CommandGroup heading="All sandboxes">
                {sandboxes?.sandboxes
                  .filter((sandbox) => sandbox.code !== selectedSandbox)
                  .map((sandbox) => (
                    <CommandItem
                      key={sandbox.code}
                      value={sandbox.code}
                      // Same rule as the Sandbox list: a Sandbox no Grant covers
                      // stays visible but cannot be switched to from here either.
                      disabled={!canEnterSandbox(sandbox.code)}
                      onSelect={() => {
                        handleSandboxChange(sandbox.code);
                      }}
                    >
                      <Check className="mr-2 h-4 w-4 opacity-0" />
                      {sandbox.name}
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
            {/* Outer gate so a viewer with neither capability is not left with a
                dangling separator above an empty group. */}
            <PermissionCheck
              oneOf={[
                Permissions.sandboxes.export,
                Permissions.sandboxes.publish,
              ]}
            >
              <CommandSeparator />
              <CommandGroup className="p-1">
                <PermissionCheck oneOf={[Permissions.sandboxes.export]}>
                  <CommandItem
                    onSelect={handleExport}
                    disabled={isLoading || !currentSandbox}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Export Sandbox
                  </CommandItem>
                </PermissionCheck>
                <PermissionCheck oneOf={[Permissions.sandboxes.publish]}>
                  <CommandItem
                    onSelect={handlePublish}
                    disabled={isLoading || !currentSandbox || !canPublish}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    <div className="flex flex-col">
                      <span>Publish Sandbox</span>
                      {!canPublish && (
                        <span className="text-xs text-muted-foreground">
                          No pending changes
                        </span>
                      )}
                    </div>
                  </CommandItem>
                </PermissionCheck>
              </CommandGroup>
            </PermissionCheck>
          </Command>
        </PopoverContent>
      </Popover>
      {publishResult && (
        <SandboxPublishSummaryDialog
          open={!!publishResult}
          onOpenChange={(open) => {
            if (!open) setPublishResult(null);
          }}
          response={publishResult}
        />
      )}
    </div>
  );
};
